import { zodResolver } from "@hookform/resolvers/zod";
import { notifications } from "@marketplace/ui/notifications";
import { getRouteApi } from "@tanstack/react-router";
import { type BaseSyntheticEvent, useMemo } from "react";
import {
  type SubmitHandler,
  useForm,
  type UseFormReturn
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useNavigationBlocker,
  usePostGeneratePresignedUrlMutation,
  usePostUploadMediaMutation
} from "@/hooks";
import { useGetProfileQuery } from "@/modules/profile";

import type { ProductCategoryType } from "../../types";
import {
  buildCreateProductPayload,
  buildDraftProductPayload
} from "../../utils/payload-builders";
import { usePostCreateProductDraftMutation } from "../mutations/use-post-create-product-draft-mutation";
import { usePostCreateProductMutation } from "../mutations/use-post-create-product-mutation";
import {
  type AddProductFormSchema,
  useAddProductFormSchema
} from "../schemas/use-add-product-form-schema";

const route = getRouteApi("/products/list/create");

type UseAddProductFormReturn = {
  formMethods: UseFormReturn<AddProductFormSchema>;
  productCategory: ProductCategoryType | undefined;
  isPending: boolean;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  onSaveDraft: () => Promise<void>;
  isSavingDraft: boolean;
};

export const useAddProductForm = (): UseAddProductFormReturn => {
  const { category } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("product", {
    keyPrefix: "addProductPage"
  });
  const addProductSchema = useAddProductFormSchema(category);
  const { data: profileData } = useGetProfileQuery();

  const companyName = useMemo(() => {
    const name = profileData?.data?.company_name;
    return name ? name : "-";
  }, [profileData]);

  const productCategory = useMemo(() => {
    return category;
  }, [category]);

  const { mutateAsync, isPending } = usePostCreateProductMutation();
  const { mutateAsync: mutateDraftAsync, isPending: isSavingDraft } =
    usePostCreateProductDraftMutation();
  const { mutateAsync: mutateUploadMediaAsync, isPending: isUploadingMedia } =
    usePostUploadMediaMutation();
  const { mutateAsync: mutateGeneratePresignedUrlAsync } =
    usePostGeneratePresignedUrlMutation();

  const defaultValues: AddProductFormSchema = {
    isRecurring: false,
    productId: "-",
    productCode: "",
    productName: "",
    productType: "",
    productService: "",
    version: "-",
    company: companyName,
    productDescription: "",
    specification: "",
    link: "",
    pictures: [],
    packages: [
      {
        name: "Package 1",
        isFreeTrial: false,
        specifications: [""],
        variants: [
          {
            quantity: 1,
            unit: "license",
            price: 25000,
            provisioningId: ""
          }
        ]
      }
    ],
    provideTrial: false,
    industryCategory: [],
    tncCheckout: "",
    tncTrial: "",
    tncRecurring: "",
    contactEmail: "-",
    apiPackage: {
      host: "",
      header: "",
      key: "",
      apis: [
        {
          name: "",
          endpoint: "",
          method: "",
          body: [{ key: "", type: "" }],
          response: ""
        }
      ]
    },
    showOnBuyer: true
  };

  const { register, control, handleSubmit, getValues, ...methods } =
    useForm<AddProductFormSchema>({
      resolver: zodResolver(addProductSchema),
      defaultValues
    });

  const {
    formState: { isDirty, isValid }
  } = methods;

  useNavigationBlocker(isDirty && !isValid);

  const uploadProductImagesIfNeeded = async () => {
    const pictures = getValues("pictures");
    if (!pictures || pictures.length === 0) return pictures;

    const uploadPromises = pictures.map(async (pic: any) => {
      if (!pic.file) return pic;

      try {
        const presignedRes = await mutateGeneratePresignedUrlAsync({
          mime_type: pic.file.type,
          size: pic.file.size,
          type: "image"
        });

        if (presignedRes?.data?.media_path) {
          const targetPath = presignedRes.data.media_path;

          mutateUploadMediaAsync({
            file: pic.file,
            type: "image",
            object_name: "product",
            object_type: "product",
            reference_no: targetPath.split("/").pop()
          }).catch((err) =>
            console.error("Background product image upload failed:", err)
          );

          return { ...pic, mediaPath: targetPath, file: undefined };
        }
      } catch (err) {
        console.error(
          "Failed to generate presigned URL for product image:",
          err
        );
      }
      return pic;
    });

    const updatedPictures = await Promise.all(uploadPromises);
    methods.setValue("pictures", updatedPictures);
    return updatedPictures;
  };

  const onSubmit: SubmitHandler<AddProductFormSchema> = async (values) => {
    try {
      const updatedPictures = await uploadProductImagesIfNeeded();
      if (updatedPictures) {
        values.pictures = updatedPictures;
      }

      const payload = buildCreateProductPayload(
        values as any,
        productCategory,
        companyName
      );

      const res = await mutateAsync(payload);

      if (res && !res.errors) {
        notifications.show({
          message: t("notifications.successCreateProduct")
        });
        navigate({ to: "/products/list" });
      }
    } catch {
      notifications.show({
        variant: "error",
        message: t("notifications.failedToCreateProduct")
      });
    }
  };

  const onSaveDraft = async () => {
    try {
      const values = getValues();

      const updatedPictures = await uploadProductImagesIfNeeded();
      if (updatedPictures) {
        values.pictures = updatedPictures;
      }

      // Include packages only if field is dirty to avoid sending untouched defaults
      const packagesDirty = Array.isArray(
        methods.formState.dirtyFields.packages
      )
        ? (methods.formState.dirtyFields.packages as unknown[]).some(Boolean)
        : false;

      const payload = buildDraftProductPayload(
        values as any,
        productCategory,
        companyName,
        undefined,
        packagesDirty
      );

      const res = await mutateDraftAsync(payload as any);

      if (res && !res.errors) {
        notifications.show({
          message: t("notifications.successSaveDraftProduct")
        });
        methods.reset(values);
        navigate({
          to: "/products/list",
          search: {
            tab: "draft"
          }
        });
      }
    } catch {
      notifications.show({
        variant: "error",
        message: t("notifications.failedToSaveDraft")
      });
    }
  };

  return {
    formMethods: {
      ...methods,
      handleSubmit,
      register,
      control,
      getValues
    },
    productCategory,
    isPending: isPending || isUploadingMedia,
    onSubmit: handleSubmit(onSubmit),
    onSaveDraft,
    isSavingDraft: isSavingDraft || isUploadingMedia
  };
};
